import { sortByString } from '~/src/dash/utils/analyticsSorter';

export interface ILiveRampNode {
  id: number;
  children: ILiveRampNode[];
  value: string;
  data: ILiveRampData | undefined | null;
  selected: boolean;
}

export interface ILiveRampData {
  DataStoreSegmentID: string;
  Name: string[];
  Description: string;
  Provider: string;
  Price: string;
}

// only keep this part for interview reference
export const LiveRampDataStoreBase = `data_store_segment_id,name,description,provider,price
1004765519,123Push > Consumer > Demographic > Household Size > 3 or more People,Individuals living in a household with 3 or more people.,123Push,0.75
1004589019,123Push > Consumer > Demographic > Homeowner/Renter > Homeowners,Individuals who are home owners. (FLA Friendly),123Push,0.75
1000846336,Alliant > Automotive > Auto - In Market New Vehicle,In-Market model for new vehicle,Alliant,2.75
1000846346,Alliant > Automotive > Auto - In Market for Insurance,In-Market model for auto insurance,Alliant,2.75
1000847476,Alliant > Automotive > Auto - In Market for Used Vehicle,In-Market model for used vehicle,Alliant,2.75
1000846216,Alliant > Demographic > Homeowner,Active multi-channel households who own their home,Alliant,2.0
1000845656,Alliant > Demographic > Age 25-29 years,Active multi-channel households with a person age 25-29 years old present in the HH.,Alliant,1.75
1000848136,Alliant > Demographic > Age 30-34 years,Active multi-channel households with a person age 30-34 years old present in the HH.,Alliant,1.75
1000845646,Alliant > Demographic > Age 35-39 years,Active multi-channel households with a person age 35-39 years old present in the HH.,Alliant,1.75
1000848146,Alliant > Demographic > Age 40-44 years,Active multi-channel households with a person age 40-44 years old present in the HH.,Alliant,1.75
1000845636,Alliant > Demographic > Age 45-49 years,Active multi-channel households with a person age 45-49 years old present in the HH.,Alliant,1.75`;

export function parseLiveRampData(customDataStore: string): ILiveRampData[] {
  const liverampDataSet = new Set('');
  const dedupLiverampData: ILiveRampData[] = [];
  const dataArray = customDataStore.split('\n');
  dataArray
    .filter((d: string) => d !== '')
    .forEach((d: string) => {
      const data = d.split(',');
      const liveRampData: ILiveRampData = {
        DataStoreSegmentID: data[0] || '',
        Name: data[1].split(' > ') || ([] as string[]),
        Description: data.slice(2, data.length - 2).join(',') || '',
        Provider: data[data.length - 2] || '',
        Price: data[data.length - 1] || '',
      };

      if (
        liveRampData.DataStoreSegmentID !== '' &&
        !liverampDataSet.has(liveRampData.DataStoreSegmentID)
      ) {
        liverampDataSet.add(liveRampData.DataStoreSegmentID);
        dedupLiverampData.push(liveRampData);
      }
    });

  return dedupLiverampData;
}

export function parseLiveRampTree(rawData: ILiveRampData[]): ILiveRampNode[] {
  let counter = 1;
  const liverampTree = [] as ILiveRampNode[];
  for (const d of rawData) {
    let currentLevel = liverampTree;
    for (let i = 0; i < d.Name.length; i += 1) {
      if (currentLevel.length === 0) {
        const newNode: ILiveRampNode = Object.assign(
          {
            id: counter,
            children: [] as ILiveRampNode[],
            value: d.Name[i],
            selected: true,
          },
          i === d.Name.length - 1 ? { data: d } : { data: undefined },
        );
        currentLevel.push(newNode);
        counter++;
        if (i !== d.Name.length - 1) {
          currentLevel = newNode.children;
        }
      } else {
        for (let j = 0; j < currentLevel.length; j += 1) {
          if (currentLevel[j].value === d.Name[i]) {
            currentLevel = currentLevel[j].children;
            break;
          } else {
            if (j === currentLevel.length - 1) {
              const newNode: ILiveRampNode = Object.assign(
                {
                  id: counter,
                  children: [] as ILiveRampNode[],
                  value: d.Name[i],
                  selected: true,
                },
                i === d.Name.length - 1 ? { data: d } : { data: undefined },
              );
              currentLevel.push(newNode);
              counter++;
              if (i !== d.Name.length - 1) {
                currentLevel = newNode.children;
              }
            }
          }
        }
      }
    }
  }

  return liverampTree;
}

const firstParty: string = 'First Party';
export function sortLiveRampTree(liverampTree: ILiveRampNode[]): ILiveRampNode[] {
  if (liverampTree.length > 0) {
    liverampTree.sort((a: ILiveRampNode, b: ILiveRampNode) => {
      const aVal: number = a.value === firstParty ? 1 : 0;
      const bVal: number = b.value === firstParty ? 1 : 0;
      return bVal - aVal || sortByString(a.value, b.value);
    });

    // Recursively sort children nodes
    liverampTree = liverampTree.map((rampNode: ILiveRampNode) => {
      rampNode.children = sortLiveRampTree(rampNode.children);
      return rampNode;
    });
  }

  return liverampTree;
}

const selectAllNodes = (node: ILiveRampNode) => {
  node.selected = true;
  if (node.children) {
    node.children.forEach((child) => {
      selectAllNodes(child);
    });
  }
};

const searchTree = (node: ILiveRampNode, searchValue: string): boolean => {
  if (node.value.toLowerCase().includes(searchValue)) {
    // node is already selected true so no need to change here
    return true;
  }
  if (node.data) {
    const searchDescription = node.data.Description.toLowerCase().includes(searchValue);
    const searchSegmentId = node.data.DataStoreSegmentID.toLowerCase().includes(searchValue);
    const searchPrice = node.data.Price.toLowerCase().includes(searchValue);
    const searchProvider = node.data.Provider.toLowerCase().includes(searchValue);

    if (searchDescription || searchSegmentId || searchPrice || searchProvider) {
      return true;
    }
  }

  if (node.children) {
    let found = false;
    node.children.forEach((child) => {
      // check the search value in each child
      if (searchTree(child, searchValue)) {
        found = true;
      }
    });
    if (found) {
      return true;
    }
  }
  // if didn't return from the above logic, set selected to false, since the node doesn't contain the search value.
  node.selected = false;
  return false;
};

export const searchLiveRampTree = (liverampTree: ILiveRampNode[], searchValue: string) => {
  liverampTree.map((node) => {
    selectAllNodes(node);
    searchTree(node, searchValue);
  });
  return liverampTree;
};
